import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ShieldCheckIcon } from 'lucide-react';
import { Logo } from '../../components/brand/Logo';
import { Button } from '../../components/ui/Button';
import { Field, inputClass } from '../../components/ui/Field';
import { Seo } from '../../components/Seo';
import { usePortalAuth } from '../../context/PortalAuth';
export function PortalLogin() {
  const {
    user,
    signIn,
    completeMfa,
    pendingMfa
  } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [code, setCode] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [magicSent, setMagicSent] = React.useState(false);
  if (user) return <Navigate to="/portal/dashboard" replace data-id="element-1340" data-mp="2ippppmi012r6" />;
  const onSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await signIn(email, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'We could not sign you in.');
      return;
    }
    if (!result.mfaRequired) navigate('/portal/dashboard');
  };
  const onMfa = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await completeMfa(code);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'That code is not valid.');
      return;
    }
    navigate('/portal/dashboard');
  };
  return <>
      <Seo title="Client login" description="Sign in to the Cavalla Solution client portal." path="/portal/login" noIndex data-id="element-1341" data-mp="3dnn3g5epeq5d" />
      <div className="flex min-h-screen w-full flex-col bg-charcoal lg:flex-row" data-id="element-1342" data-mp="3ikwpdb2vwjog">
      <div className="surface-dark flex flex-col justify-between px-6 py-10 sm:px-12 lg:w-1/2 lg:py-16" data-id="element-1343" data-mp="227oppo8kzfxt">
        <Link to="/" aria-label="Cavalla Solution, home" data-id="element-1344" data-mp="278chic4mw0t3">
          <Logo variant="reversed" data-id="element-1345" data-mp="cr2tzd5ep1a7" />
        </Link>
        <div className="mt-16 max-w-md lg:mt-0" data-id="element-1346" data-mp="cqkrvzw2bsud">
          <h1 className="font-display text-[32px] leading-tight text-white sm:text-[40px]" data-id="element-1347" data-mp="7rrd8451hvsm">
            Your work with us, in one place
          </h1>
          <p className="mt-5 text-[17px] text-white/72" data-id="element-1348" data-mp="2zq60wmqav9g">
            Project status, deliverables, invoices and a direct line to the team. Access is restricted to named
            users at your organisation.
          </p>
        </div>
        <p className="mt-16 text-small text-white/45 lg:mt-0" data-id="element-1349" data-mp="3l7fjukbbnsto">
          Personal data is held in the European Union. Every download is logged.
        </p>
      </div>

      <div className="flex flex-1 items-center justify-center px-6 py-14 sm:px-12" data-id="element-1350" data-mp="3ga5m8hds4aur">
        <div className="w-full max-w-sm" data-id="element-1351" data-mp="3c829lpfdj07w">
          {pendingMfa ? <form onSubmit={onMfa} className="space-y-6" data-id="element-1352" data-mp="1egzjn2j6et2u">
              <div className="flex items-center gap-2 text-gold" data-id="element-1353" data-mp="1eedcv5440hlo">
                <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1354" data-mp="1afmeyvrtkvxi" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-1355" data-mp="2sbohrn32an4b">Second step</span>
              </div>
              <div data-id="element-1356" data-mp="2xa9jjqxypeuv">
                <h2 className="font-display text-[26px] text-ink" data-id="element-1357" data-mp="2x9q2sdk1swif">Enter your code</h2>
                <p className="mt-2 text-[15px] text-body" data-id="element-1358" data-mp="323cd3z2qoob2">
                  Open your authenticator app and enter the six digit code for Cavalla Solution.
                </p>
              </div>
              <Field id="mfa-code" label="Authentication code" required error={error ?? undefined} data-id="element-1359" data-mp="29fpzrz3pm5b0">
                <input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" required value={code} onChange={event => setCode(event.target.value)} placeholder="204815" className={`${inputClass} tracking-[0.4em]`} data-id="element-1360" data-mp="2ed8d01orf6ot" />
              </Field>
              <Button type="submit" size="lg" disabled={busy} className="w-full" data-id="element-1361" data-mp="2pifyj6wat04">
                {busy ? 'Verifying' : 'Verify and continue'}
              </Button>
              <p className="text-small text-muted" data-id="element-1362" data-mp="3sdl4g9kbowjw">Demonstration code: 204815</p>
            </form> : <form onSubmit={onSignIn} className="space-y-6" data-id="element-1363" data-mp="a8hx0i6vw8gb">
              <div data-id="element-1364" data-mp="64tpu0m5zevl">
                <h2 className="font-display text-[26px] text-ink" data-id="element-1365" data-mp="64aamrnwf4l9">Client login</h2>
                <p className="mt-2 text-[15px] text-body" data-id="element-1366" data-mp="k4cmnx9iz3sl">
                  Sign in with the email address we set your account up against.
                </p>
              </div>
              <Field id="login-email" label="Email address" required data-id="element-1367" data-mp="tuo32jaeb5gd">
                <input id="login-email" type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} className={inputClass} data-id="element-1368" data-mp="tx0f6fth2lli" />
              </Field>
              <Field id="login-password" label="Password" required error={error ?? undefined} data-id="element-1369" data-mp="1hht1dhag41la">
                <input id="login-password" type="password" autoComplete="current-password" required value={password} onChange={event => setPassword(event.target.value)} className={inputClass} data-id="element-1370" data-mp="1mh5ps6gytgpz" />
              </Field>
              <Button type="submit" size="lg" disabled={busy} className="w-full" data-id="element-1371" data-mp="yqi8wvp2ngra">
                {busy ? 'Signing in' : 'Sign in'}
              </Button>

              <div className="flex flex-wrap items-center justify-between gap-3 text-[14px]" data-id="element-1372" data-mp="ouf1pbfd4rxh">
                <button type="button" onClick={() => setMagicSent(true)} className="text-royal underline underline-offset-4" data-id="element-1373" data-mp="trypp31v4j2m">
                  Email me a sign in link
                </button>
                <Link to="/contact" className="text-royal underline underline-offset-4" data-id="element-1374" data-mp="ktv2m2b8be8p">
                  Forgotten your password
                </Link>
              </div>
              {magicSent && <p role="status" className="rounded-sm bg-champagne p-3 text-[14px] text-body" data-id="element-1375" data-mp="2cref6beznew6">
                  If that address has an account, a single use sign in link is on its way. It expires in fifteen
                  minutes.
                </p>}

              <div className="rounded-sm border border-line bg-mist p-4 text-[13px] text-muted" data-id="element-1376" data-mp="35faiw02z672g">
                <p className="font-medium text-ink" data-id="element-1377" data-mp="30hs7m31hjfra">Demonstration account</p>
                <p className="mt-1" data-id="element-1378" data-mp="2we2mxonxlibj">a.mensah@northbridge.org</p>
                <p data-id="element-1379" data-mp="2rgkl0i8c03d1">cavalla2026, then code 204815</p>
              </div>
            </form>}

          <p className="mt-8 text-small text-muted" data-id="element-1380" data-mp="1l0rt30lsrlfn">
            <Link to="/" className="underline underline-offset-4 hover:text-royal" data-id="element-1381" data-mp="1l1jrskaoxza5">
              Return to the website
            </Link>
          </p>
        </div>
      </div>
      </div>
    </>;
}